import { useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import type { Session } from "../../app/App";
import { useNames, useRatings, useSettings, refreshRatings } from "../../data/hooks";
import { clearRating, saveRating } from "../../domain/rating";
import { isDisqualifiedByPartner } from "../../domain/status";
import type {
  Gender,
  NameRecord,
  RatingRecord,
  RatingScore,
} from "../../domain/types";
import { RatingButtons } from "../shared/RatingButtons";

type QueueMode = "all" | "safe" | "my";

const seededShuffle = <T,>(items: readonly T[], seed: string): readonly T[] => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (Math.imul(hash, 31) + seed.charCodeAt(i)) >>> 0;
  }
  let state = hash || 1;
  const rand = (): number => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 0xffffffff;
  };
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = next[i]!;
    next[i] = next[j]!;
    next[j] = tmp;
  }
  return next;
};

export const SwipePage = ({
  session,
}: {
  readonly session: Session;
}): ReactElement => {
  const { client, user } = session;
  const names = useNames(client);
  const ratings = useRatings(client);
  const settings = useSettings(client);

  const [gender, setGender] = useState<Gender | "all">("all");
  const [mode, setMode] = useState<QueueMode>("all");

  const eligible = useMemo(() => {
    if (ratings.status !== "ready" || names.status !== "ready") {
      return [];
    }
    const partnerId = ratings.data.find(
      (rating) => rating.user_id !== user.id,
    )?.user_id;
    return names.data.filter((name) => {
      const nameRatings = ratings.data.filter(
        (rating) => rating.name_id === name.id,
      );
      const mine = nameRatings.find((rating) => rating.user_id === user.id);
      const partner = nameRatings.find(
        (rating) => rating.user_id === partnerId,
      );
      if (mode === "my") {
        return mine !== undefined;
      }
      return (
        mine === undefined &&
        (gender === "all" || name.gender === gender) &&
        (mode === "all" || !isDisqualifiedByPartner(partner))
      );
    });
  }, [gender, mode, names, ratings, user.id]);

  const eligibleKey = eligible.map((name) => name.id).join(",");
  const queue = useMemo(
    () => seededShuffle(eligible, eligibleKey),
    [eligible, eligibleKey],
  );

  if (names.status === "error" || ratings.status === "error" || settings.status === "error") {
    return <Alert severity="error">Unable to load the queue.</Alert>;
  }
  if (names.status === "loading" || ratings.status === "loading" || settings.status === "loading") {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  const current = queue[0];
  const existing =
    current === undefined
      ? undefined
      : ratings.data.find(
          (r) => r.name_id === current.id && r.user_id === user.id,
        );

  return (
    <Stack spacing={3} sx={{ maxWidth: 720, mx: "auto" }}>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Pick a name
        </Typography>
        <Typography color="text.secondary">
          Rate names independently, then compare your shortlist.
        </Typography>
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <FormControl fullWidth size="small">
          <InputLabel>Queue</InputLabel>
          <Select
            label="Queue"
            value={mode}
            onChange={(event) =>
              setMode("all" === event.target.value ? "all" : "safe" === event.target.value ? "safe" : "my")
            }
          >
            <MenuItem value="all">All unrated</MenuItem>
            <MenuItem value="safe">Partner hasn't disqualified</MenuItem>
            <MenuItem value="my">My ratings</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Gender</InputLabel>
          <Select
            label="Gender"
            value={gender}
            onChange={(event) =>
              setGender(
                event.target.value === "boy" ||
                  event.target.value === "girl" ||
                  event.target.value === "neutral"
                  ? event.target.value
                  : "all",
              )
            }
          >
            <MenuItem value="all">All genders</MenuItem>
            <MenuItem value="boy">Boy</MenuItem>
            <MenuItem value="girl">Girl</MenuItem>
            <MenuItem value="neutral">Neutral</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {current === undefined ? (
        <Paper sx={{ p: { xs: 4, sm: 7 }, textAlign: "center" }}>
          <Typography variant="h5">No more names!</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Visit the management page to add names.
          </Typography>
        </Paper>
      ) : (
        <NameCard
          key={current.id}
          name={current}
          session={session}
          existing={existing}
          showNotesDefault={settings.status === "ready" && settings.data.get("show_notes_default") === "true"}
        />
      )}
    </Stack>
  );
};

const NameCard = ({
  name,
  session,
  existing,
  showNotesDefault,
}: {
  readonly name: NameRecord;
  readonly session: Session;
  readonly existing: RatingRecord | undefined;
  readonly showNotesDefault: boolean;
}): ReactElement => {
  const { client, user } = session;
  const [score, setScore] = useState<RatingScore | undefined>(existing?.score);
  const [reason, setReason] = useState(existing?.reason ?? "");
  const [notesVisible, setNotesVisible] = useState(showNotesDefault);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  const handleClear = async (): Promise<void> => {
    if (existing === undefined) {
      return;
    }
    setClearing(true);
    setSubmitError(undefined);
    try {
      await clearRating(client, existing);
      await refreshRatings(client);
    } catch (cause: unknown) {
      setSubmitError(
        cause instanceof Error ? cause.message : "Unable to clear rating.",
      );
      setClearing(false);
    }
  };

  const next = async (): Promise<void> => {
    if (score === undefined) {
      return;
    }
    setSaving(true);
    setSubmitError(undefined);
    try {
      await saveRating(
        client,
        name.id,
        user.id,
        score,
        reason.trim(),
        existing,
      );
      await refreshRatings(client);
    } catch (cause: unknown) {
      setSubmitError(
        cause instanceof Error ? cause.message : "Unable to save rating.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 3, sm: 6 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" color="primary">
            {name.gender}
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{ fontWeight: 700, fontSize: { xs: "3rem", sm: "4.5rem" } }}
          >
            {name.name}
          </Typography>
          {name.notes !== "" && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={() => setNotesVisible((visible) => !visible)}
                endIcon={
                  <ExpandMoreIcon
                    sx={{
                      transform: notesVisible ? "rotate(180deg)" : undefined,
                    }}
                  />
                }
              >
                {notesVisible ? "Hide notes" : "Show notes"}
              </Button>
              {notesVisible && (
                <Typography sx={{ mt: 2, whiteSpace: "pre-wrap" }}>
                  {name.notes}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {submitError !== undefined && (
          <Alert severity="error">{submitError}</Alert>
        )}
        <RatingButtons value={score} onChange={setScore} disabled={saving || clearing} />
        {existing !== undefined && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => void handleClear()}
            disabled={clearing || saving}
            startIcon={<CloseIcon />}
          >
            {clearing ? "Clearing…" : "Clear rating"}
          </Button>
        )}
        <TextField
          label="Reason (optional)"
          value={reason}
          disabled={saving || clearing}
          multiline
          minRows={3}
          onChange={(event) => setReason(event.target.value)}
          placeholder="What made you feel that way?"
        />
        <Button
          variant="contained"
          size="large"
          disabled={score === undefined || saving}
          onClick={() => void next()}
        >
          {saving ? "Saving…" : "Next name"}
        </Button>
      </Stack>
    </Paper>
  );
};
